/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { InStoreAsset, InStoreSensor } from './types'

/**
 * A location heads its sensors, so the list is one flat array: the location (its floor
 * plan), a summary of its sensors (its detail window), then each sensor.
 */
export type ListRow =
  | { kind: 'location'; key: string; asset: InStoreAsset }
  | { kind: 'summary'; key: string; asset: InStoreAsset }
  | { kind: 'sensor'; key: string; asset: InStoreAsset; sensor: InStoreSensor }

/** Door sensors head their location's sensors; the API's order is kept otherwise. */
const doorsFirst = (sensors: InStoreSensor[]): InStoreSensor[] => [
  ...sensors.filter((sensor) => sensor.type === 'door'),
  ...sensors.filter((sensor) => sensor.type !== 'door'),
]

export const buildRows = (locations: InStoreAsset[]): ListRow[] =>
  locations.flatMap((asset) => [
    { kind: 'location' as const, key: `location:${asset.id}`, asset },
    { kind: 'summary' as const, key: `summary:${asset.id}`, asset },
    ...doorsFirst(asset.sensors).map((sensor) => ({
      kind: 'sensor' as const,
      key: `sensor:${sensor.id}`,
      asset,
      sensor,
    })),
  ])

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

/** "3 sensors, 1 door": the temperature sensors, then the doors when there are any. */
export const sensorSummary = (asset: InStoreAsset): string => {
  const doors = asset.sensors.filter((sensor) => sensor.type === 'door').length
  const temperatures = asset.sensors.length - doors
  return [plural(temperatures, 'sensor'), ...(doors > 0 ? [plural(doors, 'door')] : [])].join(', ')
}
